import { apiRequest } from "../client";
import type { ImageUploadRequest, ImageUploadResponse } from "./types";
import { isApiImageProxyUrl, toProxyUrl } from "@/utils/imageUrl";

/** Map a proxied image URL to a path relative to the API client base (`/images/...`). */
function toImageApiRequestPath(proxiedUrl: string): string {
  if (proxiedUrl.startsWith("http")) {
    const parsed = new URL(proxiedUrl);
    parsed.searchParams.delete("token");
    const query = parsed.search;
    return parsed.pathname.replace(/^\/api\/v1/, "") + query;
  }
  const [pathPart, queryPart] = proxiedUrl.split("?");
  const params = new URLSearchParams(queryPart ?? "");
  params.delete("token");
  const query = params.toString();
  return pathPart.replace(/^\/api\/v1/, "") + (query ? `?${query}` : "");
}

/**
 * Fetch an authenticated API image via axios (session cookies / Bearer) and
 * return an object URL suitable for `<img src>`.
 */
export async function fetchImageAsBlobUrl(
  sourceUrl: string
): Promise<string | null> {
  const proxied = toProxyUrl(sourceUrl);
  if (!proxied || !isApiImageProxyUrl(proxied)) {
    return null;
  }

  try {
    const blob = await apiRequest<Blob>({
      method: "GET",
      url: toImageApiRequestPath(proxied),
      responseType: "blob",
    });
    if (!blob || blob.size === 0) {
      return null;
    }
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Upload an image file to the server
 * @param file - The image file to upload
 * @param options - Additional options for the upload
 * @returns Promise with the uploaded image details
 */
export async function uploadImage(
  file: File,
  options: Omit<ImageUploadRequest, "file"> = {}
): Promise<ImageUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);

  if (options.process_in_background !== undefined) {
    formData.append(
      "process_in_background",
      String(options.process_in_background)
    );
  }

  return apiRequest<ImageUploadResponse>({
    method: "POST",
    url: "/images/upload",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}
